/* eslint-disable no-nested-ternary */
// QuestBuilder.js
import React, { useEffect, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import { getPresignedUrl, uploadFile } from "../../../../api/FilesController";
import UploadFile from "../../../../components/common/fileUploader/FileUploaderInput";

import Title from "../../components/Title";
import Button from "../../../../components/common/button/Button";
import Banner from "../../../../components/layout/ui/Banner";
import { setColor, setFont } from "../../../../assets/styles/styles";
import { ReactComponent as TrashIcon } from "../../../../assets/images/icons/dustbin.svg";

import {
    getQuestWithSteps,
    createQuest,
    updateQuest,
    toggleQuestActive,
    createQuestStep,
    updateQuestStep,
    deleteQuestStep,
    reorderQuestSteps,
    getAllQuestsAdmin,
    deleteQuestAdmin,
} from "../../../../api/QuestController";

const DEFAULT_REQUIRED_EVENTS = [
    "OPEN_GUESTBOOK",
    "EDIT_RPM_AVATAR",
    "OPEN_SMALL_IFRAME",
    "QUIZ_COMPLETE",
    "TELEPORT", // should be NEW_ROOM_LOADING:<room_id>
    "LINKOUT",
    "DYNAMIC_LINKOUT",
    "NPC_OPEN",
    "CUSTOM", // allows creating a new required_event
];

const makeEmptyStep = (orderIndex) => ({
    questStepId: null,
    step_order: orderIndex, // keep numeric order for DB
    room_id: "", // room_id for this step (first step's room_id = starting_room_id)
    prompt_text: "",
    button_label: "Continue", // the text shown on the button
    button_mode: "BUTTON_ONLY", // "BUTTON_ONLY" or "NO_BUTTON_ACTION_ONLY"
    required_event: "",
    custom_required_event: "",

    // conditional fields
    teleportDestinationRoomId: "",
    url: "",
    dynamicLinkoutAction: "",

    // optional modal content
    modalUrl: "",
    mediaType: "", // "", "VIDEO_SMALL", "VIDEO_LARGE", "GIF", "IMAGE"
    mediaUrl: "",

    // metadata - keys are added/removed dynamically
    metadata: {},
});

const QuestBuilder = () => {
    const [metaUploadErrors, setMetaUploadErrors] = useState({});

    const handleMetaUpload = async (i, key, file) => {
        try {
            // optional: clear any prior error
            setMetaUploadErrors((prev) => ({ ...prev, [`${i}_${key}`]: "" }));

            const uploadResponse = await uploadFile({ file, usageType: "questData" }, token);

            if (uploadResponse?.status !== 200) {
                console.error("File upload failed:", uploadResponse);
                setMetaUploadErrors((prev) => ({ ...prev, [`${i}_${key}`]: "Upload failed" }));
                return uploadResponse;
            }

            console.log("File uploaded successfully:", uploadResponse);

            const imageId = uploadResponse?.data?.id;
            if (!imageId) {
                setMetaUploadErrors((prev) => ({ ...prev, [`${i}_${key}`]: "Upload failed (missing file id)" }));
                return { status: "ERROR", error: "Missing file id" };
            }

            // Get presigned URL(s)
            const presignResponse = await getPresignedUrl([imageId]);
            console.log("[handleMetaUpload] presignResponse:", presignResponse);

            const presignedUrl =
                presignResponse?.data?.[0]?.url ||
                presignResponse?.[0]?.url ||
                presignResponse?.url ||
                (presignResponse?.data?.presignedUrls && presignResponse.data.presignedUrls[0]?.url);

            console.log("[handleMetaUpload] presignedUrl:", presignedUrl);

            // Fallback if API doesn't return url how we expect:
            const valueToStore = presignedUrl;

            // ✅ store the presigned URL in step metadata (or fallback to imageId)

            setStepMetadata(i, key, valueToStore);
            // Debug: log the new value in metadata
            setTimeout(() => {
                console.log(`[handleMetaUpload] After upload, step ${i} metadata:`, steps[i]?.metadata);
            }, 100);
            // Force a state update to ensure UI refresh (in case setStepMetadata doesn't trigger re-render)
            setSteps((prev) => [...prev]);

            // ✅ clear error on success
            setMetaUploadErrors((prev) => ({ ...prev, [`${i}_${key}`]: "" }));

            // return something useful to the caller
            return { status: 200, imageId, presignedUrl, uploadResponse, presignResponse };
        } catch (err) {
            console.error("Error uploading meta file:", err);
            setMetaUploadErrors((prev) => ({ ...prev, [`${i}_${key}`]: "Upload failed" }));
            return { status: "ERROR", error: err };
        }
    };

    const { questId } = useParams(); // route like /admin/quests/:questId
    const navigate = useNavigate();
    const token = useSelector((state) => state?.user?.token);

    const isEditMode = !!questId;
    console.log(
        `[QuestBuilder] Component mounted - Mode: ${isEditMode ? "EDIT" : "CREATE"}${isEditMode ? `, questId: ${questId}` : ""}`
    );

    const [openBanner, setOpenBanner] = useState("");
    const [bannerMessage, setBannerMessage] = useState("");
    const [bannerType, setBannerType] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const [reload, setReload] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleGoBack = () => {
        navigate("/console/quest-dashboard");
    };

    const [quest, setQuest] = useState({
        questId: questId || null,
        name: "",
        description: "",
        starting_room_id: "",
        isActive: true,
    });

    const [steps, setSteps] = useState([makeEmptyStep(1)]);

    // Room conflict state for "one active quest per room" protection
    const [roomConflict, setRoomConflict] = useState(null);
    const [showConflictModal, setShowConflictModal] = useState(false);

    // Delete modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteText, setDeleteText] = useState("");
    const [deleteError, setDeleteError] = useState("");

    // drag state
    const [dragIndex, setDragIndex] = useState(null);

    const requiredEventOptions = useMemo(() => DEFAULT_REQUIRED_EVENTS, []);

    // Helper function to parse required_event from DB and determine UI state
    const parseRequiredEventFromDB = (rawRequiredEvent) => {
        const event = (rawRequiredEvent || "").trim();

        // Case 1: BUTTON_ONLY - should select Button Only radio
        if (event === "BUTTON_ONLY") {
            return {
                button_mode: "BUTTON_ONLY",
                required_event: "",
                custom_required_event: "",
                teleportDestinationRoomId: "",
            };
        }

        // Case 2: NEW_ROOM_LOADING:<room_id> - should show as TELEPORT with room_id extracted
        if (event.startsWith("NEW_ROOM_LOADING:")) {
            const roomId = event.replace("NEW_ROOM_LOADING:", "").trim();
            return {
                button_mode: "BUTTON_ONLY",
                required_event: "TELEPORT",
                custom_required_event: "",
                teleportDestinationRoomId: roomId,
            };
        }

        // Case 2.5: OPEN_LINKOUT - map back to LINKOUT for UI (LINKOUT needs a button)
        if (event === "OPEN_LINKOUT") {
            return {
                button_mode: "NO_BUTTON_ACTION_ONLY",
                required_event: "LINKOUT",
                custom_required_event: "",
                teleportDestinationRoomId: "",
            };
        }

        // Case 3: Event is in our dropdown options - use it directly
        // Exclude "CUSTOM" from this check since it's a special UI option
        const dropdownOptions = DEFAULT_REQUIRED_EVENTS.filter((e) => e !== "CUSTOM");
        if (dropdownOptions.includes(event)) {
            return {
                button_mode: "NO_BUTTON_ACTION_ONLY",
                required_event: event,
                custom_required_event: "",
                teleportDestinationRoomId: "",
            };
        }

        // Case 4: Event is not in dropdown options - treat as CUSTOM
        if (event) {
            return {
                button_mode: "NO_BUTTON_ACTION_ONLY",
                required_event: "CUSTOM",
                custom_required_event: event,
                teleportDestinationRoomId: "",
            };
        }

        // Case 5: No event - default to BUTTON_ONLY
        return {
            button_mode: "BUTTON_ONLY",
            required_event: "",
            custom_required_event: "",
            teleportDestinationRoomId: "",
        };
    };

    const fetchQuest = async () => {
        if (!questId) {
            console.log("[QuestBuilder] No questId provided - starting fresh quest");
            return;
        }
        console.log(`[QuestBuilder] Fetching quest data for questId: ${questId}`);
        setErrorMessage("");
        setIsLoading(true);

        try {
            const response = await getQuestWithSteps(token, questId);
            if (response?.status === 200) {
                console.log(`[QuestBuilder] Successfully fetched quest data:`, response.data);

                // Handle different possible response structures
                // Backend might return: { quest, steps } OR { data: { quest, steps } } OR quest directly
                const responseData = response.data;
                let q = {};
                let s = [];

                if (responseData?.quest) {
                    // Structure: { quest: {...}, steps: [...] }
                    q = responseData.quest;
                    s = responseData.steps || [];
                } else if (responseData?.data?.quest) {
                    // Structure: { data: { quest: {...}, steps: [...] } }
                    q = responseData.data.quest;
                    s = responseData.data.steps || [];
                } else if (responseData?.questId || responseData?.name) {
                    // Structure: quest object directly at response.data level
                    q = responseData;
                    s = responseData.steps || [];
                }

                // Debug: log all potential starting room ID fields and full quest object
                console.log(`[QuestBuilder] Quest object:`, q);
                console.log(`[QuestBuilder] Quest starting_room_id fields:`, {
                    starting_room_id: q.starting_room_id,
                    startingRoomId: q.startingRoomId,
                    questKeys: Object.keys(q),
                });

                // Sort steps first so we can get first step's room_id as fallback
                const sorted = [...s].sort((a, b) => (a.step_order ?? 0) - (b.step_order ?? 0));

                // Resolve starting_room_id: try quest fields first, then fallback to first step's room_id
                let startingRoomId = q.starting_room_id || q.startingRoomId || "";
                if (!startingRoomId && sorted.length > 0) {
                    startingRoomId = sorted[0].room_id || sorted[0].roomId || "";
                    console.log(
                        `[QuestBuilder] starting_room_id not found on quest, using first step's room_id:`,
                        startingRoomId
                    );
                }

                setQuest({
                    questId: q.questId || q.id || questId,
                    name: q.name || "",
                    description: q.description || "",
                    starting_room_id: startingRoomId,
                    isActive:
                        q.isActive !== undefined ? !!q.isActive : q.is_active !== undefined ? !!q.is_active : true,
                });

                // normalize steps sorted
                const normalized = sorted.map((st, idx) => {
                    const isVideoLarge = st?.metadata?.videoLarge === true || st?.metadata?.videoSize === "large";

                    // Only include keys that exist in the DB
                    const meta = {};
                    if (st?.metadata?.celebrate !== undefined) meta.celebrate = st.metadata.celebrate;
                    if (st?.metadata?.gif !== undefined) meta.gif = st.metadata.gif;
                    if (st?.metadata?.video !== undefined) meta.video = st.metadata.video;
                    if (st?.metadata?.videoLarge !== undefined) meta.videoLarge = isVideoLarge;
                    if (st?.metadata?.image !== undefined) meta.image = st.metadata.image;
                    if (st?.metadata?.url !== undefined) meta.url = st.metadata.url;

                    // Parse the required_event from DB to determine UI state
                    const rawRequiredEvent = st.required_event ?? st.requiredEvent ?? "";
                    const parsedEvent = parseRequiredEventFromDB(rawRequiredEvent);

                    console.log(
                        `[QuestBuilder] Step ${idx + 1} raw required_event: "${rawRequiredEvent}" -> parsed:`,
                        parsedEvent
                    );

                    // For TELEPORT, also check if teleportDestinationRoomId was stored separately
                    let teleportRoomId = parsedEvent.teleportDestinationRoomId;
                    if (!teleportRoomId && parsedEvent.required_event === "TELEPORT") {
                        teleportRoomId = st.teleportDestinationRoomId ?? st.teleport_destination_room_id ?? "";
                    }

                    // For LINKOUT and OPEN_SMALL_IFRAME, load URL from metadata or step field
                    let stepUrl = st.url ?? "";
                    if (
                        (parsedEvent.required_event === "LINKOUT" ||
                            parsedEvent.required_event === "OPEN_SMALL_IFRAME") &&
                        st?.metadata?.url
                    ) {
                        stepUrl = st.metadata.url;
                        // Also ensure it's in the metadata object for display
                        meta.url = st.metadata.url;
                    }

                    return {
                        questStepId: st.questStepId ?? st.id ?? null,
                        step_order: st.step_order ?? idx + 1,
                        room_id: st.room_id ?? st.roomId ?? "",
                        prompt_text: st.prompt_text ?? st.promptText ?? "",
                        button_label: st.button_label ?? st.buttonLabel ?? "Continue",
                        button_mode: parsedEvent.button_mode,
                        required_event: parsedEvent.required_event,
                        custom_required_event: parsedEvent.custom_required_event,
                        teleportDestinationRoomId: teleportRoomId,
                        url: stepUrl,
                        dynamicLinkoutAction: st.dynamicLinkoutAction ?? st.dynamic_linkout_action ?? "",
                        modalUrl: st.modalUrl ?? st.modal_url ?? "",
                        mediaType: st.mediaType ?? st.media_type ?? "",
                        mediaUrl: st.mediaUrl ?? st.media_url ?? "",
                        metadata: meta,
                    };
                });

                setSteps(normalized.length ? normalized : [makeEmptyStep(1)]);
            } else {
                setErrorMessage("Error loading quest / steps");
                setOpenBanner("ERROR");
                setBannerType("ERROR");
                setBannerMessage("Error loading quest / steps");
            }
        } catch (e) {
            console.error("[QuestBuilder] Error fetching quest:", e);
            setErrorMessage("Error loading quest / steps");
            setOpenBanner("ERROR");
            setBannerType("ERROR");
            setBannerMessage("Error loading quest / steps");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchQuest();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questId, reload]);

    // Debounced effect to check for active quest conflicts when starting_room_id changes
    // Uses getAllQuestsAdmin to fetch all quests and check if any active quest has the same starting_room_id
    // Also checks first step's room_id as fallback for quests without starting_room_id
    useEffect(() => {
        const roomId = quest.starting_room_id?.trim();
        console.log("[QuestBuilder] Room conflict check - roomId:", roomId, "current questId:", quest.questId);

        if (!roomId) {
            setRoomConflict(null);
            return;
        }

        const timeoutId = setTimeout(async () => {
            try {
                const response = await getAllQuestsAdmin(token);
                console.log("[QuestBuilder] getAllQuestsAdmin response:", response);

                if (response?.status === 200) {
                    const allQuests = response?.data?.quests || response?.data || [];
                    console.log("[QuestBuilder] All quests:", allQuests);

                    // Filter to only active quests (excluding current quest)
                    const activeQuests = allQuests.filter((q) => {
                        const qId = q.questId || q.id;
                        const qIsActive = q.isActive ?? q.is_active ?? false;
                        return qIsActive && qId !== quest.questId;
                    });

                    console.log("[QuestBuilder] Active quests (excluding current):", activeQuests);

                    // For each active quest, resolve starting_room_id (from quest or first step)
                    const questsWithRooms = await Promise.all(
                        activeQuests.map(async (q) => {
                            const qId = q.questId || q.id;
                            let qStartingRoom = (q.starting_room_id || q.startingRoomId || "").trim();

                            // If starting_room_id is empty, fetch the quest steps to get first step's room_id
                            if (!qStartingRoom) {
                                try {
                                    const stepsResponse = await getQuestWithSteps(token, qId);
                                    if (stepsResponse?.status === 200) {
                                        const steps = stepsResponse?.data?.steps || [];
                                        if (steps.length > 0) {
                                            // Sort by step_order and get first step's room_id
                                            const sortedSteps = [...steps].sort(
                                                (a, b) => (a.step_order ?? 0) - (b.step_order ?? 0)
                                            );
                                            qStartingRoom = (
                                                sortedSteps[0].room_id ||
                                                sortedSteps[0].roomId ||
                                                ""
                                            ).trim();
                                        }
                                    }
                                } catch (err) {
                                    console.error("[QuestBuilder] Error fetching steps for quest:", qId, err);
                                }
                            }

                            return { ...q, resolvedStartingRoom: qStartingRoom };
                        })
                    );

                    // Find first quest that matches the room
                    const conflictQuest = questsWithRooms.find((q) => {
                        console.log("[QuestBuilder] Checking quest:", {
                            qId: q.questId || q.id,
                            qStartingRoom: q.resolvedStartingRoom,
                            name: q.name,
                            matchesRoom: q.resolvedStartingRoom === roomId,
                        });

                        return q.resolvedStartingRoom === roomId;
                    });

                    console.log("[QuestBuilder] Conflict quest found:", conflictQuest);

                    if (conflictQuest) {
                        setRoomConflict({
                            questId: conflictQuest.questId || conflictQuest.id,
                            name: conflictQuest.name || "Unnamed Quest",
                        });
                    } else {
                        setRoomConflict(null);
                    }
                } else {
                    console.log("[QuestBuilder] getAllQuestsAdmin returned non-200 or null");
                    setRoomConflict(null);
                }
            } catch (err) {
                console.error("[QuestBuilder] Error checking room conflict:", err);
                setRoomConflict(null);
            }
        }, 300);

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quest.starting_room_id, quest.questId, token]);

    // ---------------------------
    // Quest handlers
    // ---------------------------
    const handleQuestInput = (e) => {
        const key = e.target.name;
        const value = e.target.value;
        console.log(`[QuestBuilder] Quest field changed - ${key}:`, value);
        setQuest((prev) => ({ ...prev, [key]: value }));
    };

    // Handler to deactivate a conflicting quest
    const handleDeactivateConflict = async () => {
        if (!roomConflict) return;

        console.log(`[QuestBuilder] Deactivating conflicting quest: ${roomConflict.questId}`);
        try {
            const response = await toggleQuestActive(token, roomConflict.questId, false);
            if (response?.status === 200 || response?.status === 204) {
                setBannerMessage(`Deactivated conflicting quest: ${roomConflict.name}`);
                setOpenBanner("MESSAGE");
                setRoomConflict(null);
                setShowConflictModal(false);
            } else {
                setBannerMessage("Error deactivating conflicting quest");
                setOpenBanner("ERROR");
            }
        } catch (err) {
            console.error("[QuestBuilder] Error deactivating conflicting quest:", err);
            setBannerMessage("Error deactivating conflicting quest");
            setOpenBanner("ERROR");
        }
    };

    // Helper function to check for room conflict before activation
    const checkRoomConflictBeforeActivate = async () => {
        const roomId = quest.starting_room_id?.trim();
        if (!roomId) return null;

        try {
            const response = await getAllQuestsAdmin(token);
            if (response?.status !== 200) return null;

            const allQuests = response?.data?.quests || response?.data || [];

            // Filter to only active quests (excluding current quest)
            const activeQuests = allQuests.filter((q) => {
                const qId = q.questId || q.id;
                const qIsActive = q.isActive ?? q.is_active ?? false;
                return qIsActive && qId !== quest.questId;
            });

            // For each active quest, resolve starting_room_id (from quest or first step)
            const questsWithRooms = await Promise.all(
                activeQuests.map(async (q) => {
                    const qId = q.questId || q.id;
                    let qStartingRoom = (q.starting_room_id || q.startingRoomId || "").trim();

                    if (!qStartingRoom) {
                        try {
                            const stepsResponse = await getQuestWithSteps(token, qId);
                            if (stepsResponse?.status === 200) {
                                const fetchedSteps = stepsResponse?.data?.steps || [];
                                if (fetchedSteps.length > 0) {
                                    const sortedSteps = [...fetchedSteps].sort(
                                        (a, b) => (a.step_order ?? 0) - (b.step_order ?? 0)
                                    );
                                    qStartingRoom = (sortedSteps[0].room_id || sortedSteps[0].roomId || "").trim();
                                }
                            }
                        } catch (err) {
                            console.error("[QuestBuilder] Error fetching steps for quest:", qId, err);
                        }
                    }

                    return { ...q, resolvedStartingRoom: qStartingRoom };
                })
            );

            // Find first quest that matches the room
            const conflictQuest = questsWithRooms.find((q) => q.resolvedStartingRoom === roomId);

            if (conflictQuest) {
                return {
                    questId: conflictQuest.questId || conflictQuest.id,
                    name: conflictQuest.name || "Unnamed Quest",
                };
            }
            return null;
        } catch (err) {
            console.error("[QuestBuilder] Error checking room conflict:", err);
            return null;
        }
    };

    const handleToggleActive = async () => {
        console.log(`[QuestBuilder] Toggling quest active state from ${quest.isActive} to ${!quest.isActive}`);
        const next = !quest.isActive;

        // If trying to activate, check for room conflict first
        if (next) {
            // First check if we already have a detected conflict
            if (roomConflict) {
                console.log("[QuestBuilder] Activation blocked - room already has active quest:", roomConflict);
                setShowConflictModal(true);
                return;
            }

            // Otherwise, do a fresh check before activating
            const conflict = await checkRoomConflictBeforeActivate();
            if (conflict) {
                console.log("[QuestBuilder] Activation blocked - conflict found:", conflict);
                setRoomConflict(conflict);
                setShowConflictModal(true);
                return;
            }
        }

        // If quest isn't created yet, just toggle locally
        if (!quest.questId) {
            console.log("[QuestBuilder] Quest not yet created - toggling locally");
            setQuest((prev) => ({ ...prev, isActive: !prev.isActive }));
            return;
        }

        try {
            const response = await toggleQuestActive(token, quest.questId, next);
            console.log("[QuestBuilder] toggleQuestActive response:", response);

            if (response?.status === 200 || response?.status === 204) {
                setQuest((prev) => ({ ...prev, isActive: next }));
                setBannerMessage(next ? "Quest activated" : "Quest deactivated");
                setOpenBanner("MESSAGE");
            } else {
                // Check for ROOM_ALREADY_HAS_ACTIVE_QUEST error code from backend
                const errorData = response?.data || response?.response?.data || {};

                if (errorData?.code === "ROOM_ALREADY_HAS_ACTIVE_QUEST" && errorData?.existingQuest) {
                    console.log(
                        "[QuestBuilder] Backend returned ROOM_ALREADY_HAS_ACTIVE_QUEST:",
                        errorData.existingQuest
                    );
                    setRoomConflict({
                        questId: errorData.existingQuest.questId,
                        name: errorData.existingQuest.name || "Unnamed Quest",
                    });
                    setShowConflictModal(true);
                    return;
                }

                // Fallback: Check for 409 status
                if (response?.status === 409 || response?.response?.status === 409) {
                    console.log("[QuestBuilder] Backend returned 409 Conflict, checking for conflict...");
                    const conflict = await checkRoomConflictBeforeActivate();
                    if (conflict) {
                        setRoomConflict(conflict);
                        setShowConflictModal(true);
                        return;
                    }
                    setErrorMessage("Another quest is already active for this room");
                    setBannerMessage("Another quest is already active for this room");
                    setOpenBanner("ERROR");
                    return;
                }

                setErrorMessage("Error toggling quest active state");
                setBannerMessage("Error toggling quest active state");
                setOpenBanner("ERROR");
            }
        } catch (err) {
            console.error("[QuestBuilder] Error toggling quest active state:", err);

            // Check for ROOM_ALREADY_HAS_ACTIVE_QUEST error code from backend
            const errorData = err?.response?.data || err?.data || {};

            if (errorData?.code === "ROOM_ALREADY_HAS_ACTIVE_QUEST" && errorData?.existingQuest) {
                console.log("[QuestBuilder] Backend threw ROOM_ALREADY_HAS_ACTIVE_QUEST:", errorData.existingQuest);
                setRoomConflict({
                    questId: errorData.existingQuest.questId,
                    name: errorData.existingQuest.name || "Unnamed Quest",
                });
                setShowConflictModal(true);
                return;
            }

            // Fallback: Check for 409 Conflict status
            const errorStatus = err?.response?.status || err?.status;
            if (errorStatus === 409) {
                console.log("[QuestBuilder] Backend threw 409 Conflict, checking for conflict...");
                const conflict = await checkRoomConflictBeforeActivate();
                if (conflict) {
                    setRoomConflict(conflict);
                    setShowConflictModal(true);
                    return;
                }
                setErrorMessage("Another quest is already active for this room");
                setBannerMessage("Another quest is already active for this room");
                setOpenBanner("ERROR");
                return;
            }

            setErrorMessage("Error toggling quest active state");
            setBannerMessage("Error toggling quest active state");
            setOpenBanner("ERROR");
        }
    };

    const handleDeleteQuest = async () => {
        if (!quest.questId) {
            // Quest not yet created, just navigate back
            navigate("/console/quest-dashboard");
            return;
        }

        // Show delete confirmation modal
        setShowDeleteModal(true);
    };

    const confirmDeleteQuest = async () => {
        if (deleteText.toLowerCase() !== "delete") {
            setDeleteError("Type DELETE to proceed.");
            return;
        }

        try {
            console.log(`[QuestBuilder] Attempting to delete quest ${quest.questId}`);
            const response = await deleteQuestAdmin(token, quest.questId);

            if (response && (response.status === 204 || response.status === 200)) {
                setShowDeleteModal(false);
                setDeleteText("");
                setDeleteError("");
                setBannerMessage("Quest deleted successfully");
                setOpenBanner("MESSAGE");
                // Navigate back to dashboard after deletion
                setTimeout(() => {
                    navigate("/console/quest-dashboard");
                }, 1500);
            } else {
                setDeleteError("Error deleting quest. Please try again.");
            }
        } catch (err) {
            console.error("[QuestBuilder] Error deleting quest:", err);
            setDeleteError("Error deleting quest. Please try again.");
        }
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setDeleteText("");
        setDeleteError("");
    };

    // ---------------------------
    // Step handlers (local)
    // ---------------------------
    const setStepField = (i, key, value) => {
        console.log(`[QuestBuilder] Step ${i + 1} field changed - ${key}:`, value);
        const newSteps = JSON.parse(JSON.stringify(steps));
        newSteps[i][key] = value;

        // Keep metadata mirror if user sets media via UI
        if (key === "mediaType") {
            // reset metadata media fields when switching type
            newSteps[i].metadata.gif = "";
            newSteps[i].metadata.video = "";
            newSteps[i].metadata.image = "";
            newSteps[i].metadata.videoSize = "";
        }

        // When changing required_event to LINKOUT or OPEN_SMALL_IFRAME, initialize url in metadata if not present
        if (key === "required_event" && (value === "LINKOUT" || value === "OPEN_SMALL_IFRAME")) {
            if (newSteps[i].metadata.url === undefined) {
                newSteps[i].metadata.url = newSteps[i].url || "";
            }
        }

        // Sync url field with metadata.url for LINKOUT and OPEN_SMALL_IFRAME
        if (key === "url") {
            const reqEvent = newSteps[i].required_event;
            if (reqEvent === "LINKOUT" || reqEvent === "OPEN_SMALL_IFRAME") {
                newSteps[i].metadata.url = value;
            }
        }

        setSteps(newSteps);
    };

    const setStepMetadata = (i, key, value) => {
        setSteps((prevSteps) => {
            const newSteps = JSON.parse(JSON.stringify(prevSteps));
            newSteps[i].metadata[key] = value;

            // Sync metadata.url with step.url for LINKOUT and OPEN_SMALL_IFRAME
            if (key === "url") {
                const reqEvent = newSteps[i].required_event;
                if (reqEvent === "LINKOUT" || reqEvent === "OPEN_SMALL_IFRAME") {
                    newSteps[i].url = value;
                }
            }

            return newSteps;
        });
    };

    const addNewStep = () => {
        const nextOrder = steps.length + 1;
        console.log(`[QuestBuilder] Adding new step at position ${nextOrder}`);
        setSteps((prev) => [...prev, makeEmptyStep(nextOrder)]);
    };

    const recalcOrder = (arr) =>
        arr.map((s, idx) => ({
            ...s,
            step_order: idx + 1,
        }));

    const moveStepUp = (i) => {
        if (i === 0) {
            console.log(`[QuestBuilder] Cannot move step ${i + 1} up - already at top`);
            return;
        }
        console.log(`[QuestBuilder] Moving step ${i + 1} up to position ${i}`);
        const newSteps = JSON.parse(JSON.stringify(steps));
        const temp = newSteps[i - 1];
        newSteps[i - 1] = newSteps[i];
        newSteps[i] = temp;
        setSteps(recalcOrder(newSteps));
    };

    const moveStepDown = (i) => {
        if (i === steps.length - 1) {
            console.log(`[QuestBuilder] Cannot move step ${i + 1} down - already at bottom`);
            return;
        }
        console.log(`[QuestBuilder] Moving step ${i + 1} down to position ${i + 2}`);
        const newSteps = JSON.parse(JSON.stringify(steps));
        const temp = newSteps[i + 1];
        newSteps[i + 1] = newSteps[i];
        newSteps[i] = temp;
        setSteps(recalcOrder(newSteps));
    };

    const onDragStart = (i) => setDragIndex(i);

    const onDrop = (dropIndex) => {
        if (dragIndex === null || dragIndex === dropIndex) {
            console.log(`[QuestBuilder] Drop cancelled - dragIndex: ${dragIndex}, dropIndex: ${dropIndex}`);
            setDragIndex(null);
            return;
        }
        console.log(`[QuestBuilder] Dropping step from position ${dragIndex + 1} to position ${dropIndex + 1}`);
        const newSteps = JSON.parse(JSON.stringify(steps));
        const [moved] = newSteps.splice(dragIndex, 1);
        newSteps.splice(dropIndex, 0, moved);
        setSteps(recalcOrder(newSteps));
        setDragIndex(null);
    };

    const handleDeleteStep = async (i) => {
        const st = steps[i];
        console.log(
            `[QuestBuilder] Deleting step ${i + 1}`,
            st.questStepId ? `(questStepId: ${st.questStepId})` : "(not yet saved)"
        );

        // If step exists in DB, delete it there too
        if (st.questStepId) {
            console.log(`[QuestBuilder] Step exists in DB - calling deleteQuestStep API`);
            const response = await deleteQuestStep(token, st.questStepId);
            if (!(response?.status === 204 || response?.status === 200)) {
                console.error(`[QuestBuilder] Failed to delete step from DB:`, response);
                setErrorMessage("Error deleting step");
                setBannerMessage("Error deleting step");
                setOpenBanner("ERROR");
                return;
            }
            console.log(`[QuestBuilder] Step deleted from DB successfully`);
        }

        const newSteps = JSON.parse(JSON.stringify(steps));
        newSteps.splice(i, 1);

        setSteps(newSteps.length ? recalcOrder(newSteps) : [makeEmptyStep(1)]);
        setBannerMessage("Step deleted");
        setOpenBanner("MESSAGE");
    };

    // ---------------------------
    // API payload normalization
    // ---------------------------
    const normalizeStepForApi = (questIdToUse, step, stepIndex, allSteps) => {
        // Determine the required_event to send to the DB
        let requiredEvent;

        // Get the effective required_event from UI state
        const uiRequiredEvent = (step.required_event || "").trim();

        // TELEPORT takes priority over button_mode - if user selected TELEPORT, use that
        if (uiRequiredEvent === "TELEPORT") {
            // For TELEPORT, send NEW_ROOM_LOADING:<teleportRoomId>
            const teleportRoomId = (step.teleportDestinationRoomId || "").trim();
            requiredEvent = teleportRoomId ? `NEW_ROOM_LOADING:${teleportRoomId}` : undefined;
        } else if (uiRequiredEvent === "LINKOUT") {
            // LINKOUT needs a button - transform to OPEN_LINKOUT for quest engine
            requiredEvent = "OPEN_LINKOUT";
        } else if (step.button_mode === "BUTTON_ONLY" || !uiRequiredEvent) {
            // Button Only mode - send BUTTON_ONLY as required_event
            requiredEvent = "BUTTON_ONLY";
        } else if (uiRequiredEvent === "CUSTOM") {
            // For CUSTOM, use the custom_required_event value (must be non-empty)
            requiredEvent = (step.custom_required_event || "").trim();
        } else {
            // All other dropdown options - send as-is
            requiredEvent = uiRequiredEvent;
        }

        if (!requiredEvent) {
            requiredEvent = "BUTTON_ONLY";
        }

        // Cascading room_id logic:
        // - Start with starting_room_id
        // - After a TELEPORT step, subsequent steps use that teleport's destination
        // - Look backwards through previous steps to find the most recent TELEPORT
        let stepRoomId = quest.starting_room_id || "";

        for (let i = 0; i < stepIndex; i += 1) {
            const prevStep = allSteps[i];
            const prevEvent =
                prevStep.required_event === "CUSTOM"
                    ? (prevStep.custom_required_event || "").trim()
                    : (prevStep.required_event || "").trim();

            if (prevEvent === "TELEPORT" && prevStep.teleportDestinationRoomId?.trim()) {
                // This TELEPORT changes the room for all subsequent steps
                stepRoomId = prevStep.teleportDestinationRoomId.trim();
            }
        }

        console.log(`[QuestBuilder] Step ${stepIndex + 1} room_id resolved to: ${stepRoomId}`);

        // Build metadata object - only include fields that exist in the step's metadata
        const metadata = {};

        if (step.metadata?.celebrate !== undefined) {
            metadata.celebrate = !!step.metadata.celebrate;
        }

        if (step.metadata?.gif !== undefined && step.metadata?.gif?.trim()) {
            metadata.gif = step.metadata.gif.trim();
        }

        if (step.metadata?.video !== undefined && step.metadata?.video?.trim()) {
            metadata.video = step.metadata.video.trim();
            if (step.metadata?.videoLarge) {
                metadata.videoLarge = true;
            }
        }

        if (step.metadata?.image !== undefined && step.metadata?.image?.trim()) {
            metadata.image = step.metadata.image.trim();
        }

        // Add URL to metadata for LINKOUT and OPEN_SMALL_IFRAME
        const effectiveEvent =
            step.required_event === "CUSTOM"
                ? (step.custom_required_event || "").trim()
                : (step.required_event || "").trim();

        if (
            (effectiveEvent === "LINKOUT" || effectiveEvent === "OPEN_SMALL_IFRAME") &&
            (step.url || step.metadata?.url)
        ) {
            const urlValue = (step.url || step.metadata?.url || "").trim();
            if (urlValue) {
                metadata.url = urlValue;
            }
        } else if (step.metadata?.url !== undefined && step.metadata?.url?.trim()) {
            metadata.url = step.metadata.url.trim();
        }

        const payload = {
            questId: questIdToUse,
            questStepId: step.questStepId || undefined,

            step_order: step.step_order,
            step_index: step.step_order, // backend requires this for creating new steps
            room_id: stepRoomId,
            prompt_text: step.prompt_text,
            button_label: step.button_label || "Continue", // actual button text
            button_mode: step.button_mode,
            required_event: requiredEvent,

            // conditional fields (send only if relevant)
            teleportDestinationRoomId:
                effectiveEvent === "TELEPORT" ? (step.teleportDestinationRoomId || "").trim() : "",
            url: effectiveEvent === "LINKOUT" || effectiveEvent === "OPEN_SMALL_IFRAME" ? (step.url || "").trim() : "",
            dynamicLinkoutAction: effectiveEvent === "DYNAMIC_LINKOUT" ? (step.dynamicLinkoutAction || "").trim() : "",

            // optional modal content
            modalUrl: (step.modalUrl || "").trim(),
            mediaType: step.mediaType || "",
            mediaUrl: (step.mediaUrl || "").trim(),

            metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
        };

        return payload;
    };

    // ---------------------------
    // Save All (Quest + Steps)
    // ---------------------------
    const validate = () => {
        if (!quest.name.trim()) return "Quest name is required";
        if (!quest.starting_room_id.trim()) return "Starting Room ID is required";

        for (let i = 0; i < steps.length; i += 1) {
            const s = steps[i];
            const isButtonOnly = s.button_mode === "BUTTON_ONLY";
            const requiredEvent =
                s.required_event === "CUSTOM"
                    ? (s.custom_required_event || "").trim()
                    : (s.required_event || "").trim();

            if (!s.prompt_text.trim()) return `Step ${i + 1}: Prompt text is required`;

            // Button Only requires button_label but not required_event
            if (isButtonOnly) {
                if (!s.button_label.trim()) return `Step ${i + 1}: Button text is required for Button Only mode`;
            } else if (!requiredEvent) {
                // In-Game Action Trigger requires required_event
                return `Step ${i + 1}: Required action is required for In-Game Action Trigger`;
            }

            // Validate CUSTOM event format (uppercase, no spaces)
            if (s.required_event === "CUSTOM") {
                const customEvent = (s.custom_required_event || "").trim();
                if (!customEvent) return `Step ${i + 1}: Custom action name is required`;
                if (/\s/.test(customEvent)) return `Step ${i + 1}: Custom action cannot contain spaces`;
                if (customEvent !== customEvent.toUpperCase()) return `Step ${i + 1}: Custom action must be uppercase`;
            }

            if (requiredEvent === "TELEPORT" && !s.teleportDestinationRoomId.trim())
                return `Step ${i + 1}: Teleport Destination Room ID is required`;
            if (requiredEvent === "LINKOUT" && !s.url.trim()) return `Step ${i + 1}: URL is required for Linkout`;
            if (requiredEvent === "OPEN_SMALL_IFRAME" && !s.url.trim())
                return `Step ${i + 1}: URL is required for Open Small Iframe`;
            if (requiredEvent === "DYNAMIC_LINKOUT" && !s.dynamicLinkoutAction.trim())
                return `Step ${i + 1}: Dynamic Linkout Action is required`;
        }

        return "";
    };

    const handleSaveAll = async () => {
        console.log("[QuestBuilder] Starting save all operation...");
        setErrorMessage("");

        // Block save if quest is active and there's a room conflict
        if (quest.isActive && roomConflict) {
            const conflictMsg = "This room already has an active quest";
            setErrorMessage(conflictMsg);
            setBannerMessage(conflictMsg);
            setBannerType("ERROR");
            setOpenBanner("ERROR");
            return;
        }

        const err = validate();
        let metaErr = "";
        steps.forEach((st, i) => {
            ["gif", "image", "video"].forEach((key) => {
                if (st.metadata[key] !== undefined && !st.metadata[key]?.trim()) {
                    metaErr += `Step ${i + 1}: ${key.toUpperCase()} selected but no file uploaded. Please upload a file or deselect the option.\n`;
                }
            });
        });
        if (err || metaErr) {
            const msg = [err, metaErr].filter(Boolean).join("\n");
            setErrorMessage(msg);
            setBannerMessage(msg);
            setBannerType("ERROR");
            setOpenBanner("ERROR");
            return;
        }
        setIsSaving(true);

        try {
            let questIdToUse = quest.questId;

            // 1) Create quest if needed
            if (!questIdToUse) {
                console.log("[QuestBuilder] Creating new quest...");
                const createPayload = {
                    name: quest.name.trim(),
                    description: (quest.description || "").trim(),
                    starting_room_id: quest.starting_room_id.trim(),
                    isActive: !!quest.isActive,
                };
                console.log("[QuestBuilder] Create quest payload:", createPayload);

                const res = await createQuest(token, createPayload);
                if (!(res?.status === 201 || res?.status === 200)) {
                    throw new Error("Error creating quest");
                }

                questIdToUse = res.data?.questId || res.data?.id;
                console.log(`[QuestBuilder] Quest created successfully with ID: ${questIdToUse}`);
                if (!questIdToUse) throw new Error("Quest created but questId missing in response");

                setQuest((prev) => ({ ...prev, questId: questIdToUse }));
            } else {
                // 2) Update quest
                console.log(`[QuestBuilder] Updating existing quest ${questIdToUse}...`);
                const updatePayload = {
                    questId: questIdToUse,
                    name: quest.name.trim(),
                    description: (quest.description || "").trim(),
                    starting_room_id: quest.starting_room_id.trim(),
                    isActive: !!quest.isActive,
                };
                console.log("[QuestBuilder] Update quest payload:", updatePayload);

                const res = await updateQuest(token, updatePayload);
                if (!(res?.status === 200 || res?.status === 204)) {
                    throw new Error("Error updating quest");
                }
                console.log(`[QuestBuilder] Quest updated successfully`);
            }

            // 3) Upsert steps
            console.log(`[QuestBuilder] Processing ${steps.length} steps...`);
            const newSteps = JSON.parse(JSON.stringify(steps));
            const stepPromises = newSteps.map((step, i) => {
                const payload = normalizeStepForApi(questIdToUse, step, i, newSteps);

                console.log(`[QuestBuilder] Step ${i + 1} payload:`, payload);
                console.log(`[QuestBuilder] Step ${i + 1} required_event being saved:`, payload.required_event);

                if (!step.questStepId) {
                    console.log(`[QuestBuilder] Creating step ${i + 1}...`);
                    return createQuestStep(token, payload).then((res) => {
                        if (!(res?.status === 201 || res?.status === 200)) {
                            throw new Error(`Error creating step ${i + 1}`);
                        }
                        const newId = res.data?.questStepId || res.data?.id;
                        console.log(`[QuestBuilder] Step ${i + 1} created with ID: ${newId}`);
                        newSteps[i].questStepId = newId || step.questStepId;
                    });
                }
                console.log(`[QuestBuilder] Updating step ${i + 1} (ID: ${step.questStepId})...`);
                return updateQuestStep(token, payload).then((res) => {
                    if (!(res?.status === 200 || res?.status === 204)) {
                        throw new Error(`Error updating step ${i + 1}`);
                    }
                    console.log(`[QuestBuilder] Step ${i + 1} updated successfully`);
                });
            });
            await Promise.all(stepPromises);

            setSteps(recalcOrder(newSteps));

            // 4) Persist step ordering via reorderQuestSteps (required - backend handles unique constraint)
            console.log(`[QuestBuilder] Persisting step order to server via reorderQuestSteps...`);
            const ordered = newSteps.map((s, idx) => ({
                questStepId: s.questStepId,
                step_order: idx + 1,
            }));
            const reorderRes = await reorderQuestSteps(token, questIdToUse, ordered);
            if (!(reorderRes?.status === 200 || reorderRes?.status === 204)) {
                console.warn(`[QuestBuilder] Reorder may have failed:`, reorderRes);
            } else {
                console.log(`[QuestBuilder] Step order persisted successfully`);
            }

            setBannerMessage("Quest + steps saved successfully");
            setBannerType("SUCCESS");
            setOpenBanner("SUCCESS");

            // Navigate back to dashboard after 5 seconds to show success message
            setTimeout(() => {
                navigate("/console/quest-dashboard");
            }, 3000);
        } catch (e) {
            const msg = e?.message || "Error saving quest / steps";
            setErrorMessage(msg);
            setBannerMessage(msg);
            setBannerType("ERROR");
            setOpenBanner("ERROR");
        } finally {
            setIsSaving(false);
        }
    };

    // ---------------------------
    // UI helpers
    // ---------------------------
    const getEffectiveRequiredEvent = (st) =>
        st.required_event === "CUSTOM" ? (st.custom_required_event || "").trim() : (st.required_event || "").trim();

    // Helper to format custom event (uppercase, no spaces)
    const formatCustomEvent = (value) => {
        return value.toUpperCase().replace(/\s/g, "_");
    };

    // Build the metadata preview object for display (only existing fields)
    const buildMetadataPreview = (metadata) => {
        const preview = {};
        if (metadata?.celebrate !== undefined) {
            preview.celebrate = metadata.celebrate;
        }
        if (metadata?.gif !== undefined && metadata?.gif) {
            preview.gif = metadata.gif;
        }
        if (metadata?.video !== undefined && metadata?.video) {
            preview.video = metadata.video;
            if (metadata?.videoLarge) {
                preview.videoLarge = metadata.videoLarge;
            }
        }
        if (metadata?.image !== undefined && metadata?.image) {
            preview.image = metadata.image;
        }
        if (metadata?.url !== undefined && metadata?.url) {
            preview.url = metadata.url;
        }
        return preview;
    };

    return (
        <Wrapper>
            {/* Loading Overlay */}
            {(isLoading || isSaving) && (
                <LoadingOverlay>
                    <Spinner />
                    <LoadingText>{isSaving ? "Saving..." : "loading..."}</LoadingText>
                </LoadingOverlay>
            )}

            <BackButton onClick={handleGoBack}>← Back to Quests Dashboard</BackButton>

            <Title title={"Quest Builder"} description={"Create and manage quests and quest steps."} />

            <TopActions>
                <StatusPill active={quest.isActive} onClick={handleToggleActive} title="Click to toggle active state">
                    {quest.isActive ? "Active" : "Inactive"}
                </StatusPill>
                <ActionsRight>
                    <Button
                        margin={"0"}
                        variant="DefaultButton"
                        label={quest.isActive ? "Deactivate" : "Activate"}
                        onClick={handleToggleActive}
                    />
                    <DeleteButton onClick={handleDeleteQuest} title="Delete quest">
                        <StyledTrashIcon />
                    </DeleteButton>
                </ActionsRight>
            </TopActions>

            <QuestCard>
                {quest.questId && <QuestIdDisplay>Quest ID: {quest.questId}</QuestIdDisplay>}
                <Row2Col>
                    <FieldRow>
                        <Label>Quest Name *</Label>
                        <Input name="name" value={quest.name} onChange={handleQuestInput} placeholder="Quest name" />
                    </FieldRow>

                    <FieldRow>
                        <Label>Quest Description (optional)</Label>
                        <TextArea
                            name="description"
                            value={quest.description}
                            onChange={handleQuestInput}
                            placeholder="Optional description"
                        />
                    </FieldRow>
                </Row2Col>
                <FieldRow>
                    <Label>Starting Room ID *</Label>
                    <Input
                        name="starting_room_id"
                        value={quest.starting_room_id}
                        onChange={handleQuestInput}
                        placeholder="Starting room UUID"
                    />
                    {quest.starting_room_id?.trim() &&
                        (roomConflict ? (
                            <RoomConflictText>
                                ❌ Active quest already exists:{" "}
                                <a
                                    href={`/console/quest-builder/${roomConflict.questId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {roomConflict.name}
                                </a>
                            </RoomConflictText>
                        ) : (
                            <RoomOkText>✅ No active quest in this room.</RoomOkText>
                        ))}
                </FieldRow>
            </QuestCard>
            <StepsContainer>
                {steps.map((st, i) => {
                    const effectiveEvent = getEffectiveRequiredEvent(st);
                    const isButtonOnly = st.button_mode === "BUTTON_ONLY";
                    const isLinkout = effectiveEvent === "LINKOUT";
                    const isSmallIframe = effectiveEvent === "OPEN_SMALL_IFRAME";
                    const showButtonText = isButtonOnly || isLinkout || isSmallIframe;
                    // Collapsible state: default collapsed
                    if (st._showModalSettings === undefined) {
                        st._showModalSettings = false;
                    }
                    return (
                        <StepCard
                            key={st.questStepId || `new-${i}`}
                            draggable
                            onDragStart={() => onDragStart(i)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => onDrop(i)}
                            isDragging={dragIndex === i}
                        >
                            <StepTopRow>
                                <StepTitle>
                                    <DragHandle title="Drag to reorder">⠿</DragHandle>
                                    Step {i + 1} :{" "}
                                    <span
                                        style={{
                                            border: `2px dashed ${setColor.grey1};`,
                                            padding: "6px 16px",
                                            borderRadius: "6px",
                                            fontSize: "18px",
                                            textTransform: "none",
                                            fontWeight: "600",
                                        }}
                                    >{` ${st.prompt_text.substring(0, 15)}${st.prompt_text.length > 30 ? "..." : ""}`}</span>
                                </StepTitle>

                                <StepButtons>
                                    <SmallBtn onClick={() => moveStepUp(i)} disabled={i === 0}>
                                        ↑
                                    </SmallBtn>
                                    <SmallBtn onClick={() => moveStepDown(i)} disabled={i === steps.length - 1}>
                                        ↓
                                    </SmallBtn>
                                    <DangerBtn onClick={() => handleDeleteStep(i)}>Delete Step</DangerBtn>
                                </StepButtons>
                            </StepTopRow>

                            <StepBody>
                                <FieldBlock>
                                    <ButtonTextLabel>Quest Prompt Text *</ButtonTextLabel>
                                    <TextArea
                                        value={st.prompt_text}
                                        onChange={(e) => setStepField(i, "prompt_text", e.target.value)}
                                        placeholder="Type the prompt for this step..."
                                    />
                                </FieldBlock>

                                <FieldBlock>
                                    <ButtonTextLabel>Quest Trigger</ButtonTextLabel>
                                    <RadioRow style={{ gap: "30px", alignItems: "center" }}>
                                        <RadioItem as={st.button_mode === "BUTTON_ONLY" ? PinkRadioItem : RadioItem}>
                                            <input
                                                type="radio"
                                                name={`button_mode_${i}`}
                                                checked={st.button_mode === "BUTTON_ONLY"}
                                                onChange={() => setStepField(i, "button_mode", "BUTTON_ONLY")}
                                            />
                                            <span>Button Only</span>
                                        </RadioItem>
                                        <OrText>Or</OrText>
                                        <RadioItem
                                            as={st.button_mode === "NO_BUTTON_ACTION_ONLY" ? PinkRadioItem : RadioItem}
                                        >
                                            <input
                                                type="radio"
                                                name={`button_mode_${i}`}
                                                checked={st.button_mode === "NO_BUTTON_ACTION_ONLY"}
                                                onChange={() => setStepField(i, "button_mode", "NO_BUTTON_ACTION_ONLY")}
                                            />
                                            <span>⚡In-Game Action Trigger</span>
                                        </RadioItem>
                                    </RadioRow>
                                </FieldBlock>

                                {/* Button Only mode, LINKOUT, and OPEN_SMALL_IFRAME require button text */}
                                {showButtonText && (
                                    <FieldBlock>
                                        <ButtonTextHeader>
                                            <ButtonTextLabel>
                                                {isLinkout
                                                    ? "Linkout Button Text *"
                                                    : isSmallIframe
                                                      ? "Open Content Button Text *"
                                                      : "Button Text *"}
                                            </ButtonTextLabel>
                                            <ButtonPreviewWrapper>
                                                <PreviewLabel>Button Preview</PreviewLabel>
                                                <ButtonPreview>
                                                    {st.button_label ||
                                                        (isLinkout
                                                            ? "Open Link"
                                                            : isSmallIframe
                                                              ? "View Content"
                                                              : "Continue")}
                                                </ButtonPreview>
                                            </ButtonPreviewWrapper>
                                        </ButtonTextHeader>
                                        <Input
                                            value={st.button_label}
                                            onChange={(e) => setStepField(i, "button_label", e.target.value)}
                                            placeholder={
                                                isLinkout
                                                    ? "e.g. Visit Website, Learn More, Open Link"
                                                    : isSmallIframe
                                                      ? "e.g. View Content, Open, Learn More"
                                                      : "e.g. Continue, Next, Got it!"
                                            }
                                        />
                                    </FieldBlock>
                                )}

                                {/* In-Game Action Trigger mode requires required_event */}
                                {!isButtonOnly && (
                                    <FieldBlock>
                                        <FieldLabelWithHelper>
                                            <ButtonTextLabel>Required Action *</ButtonTextLabel>
                                            <HelperText>
                                                "This will progress the step only if this action is sent from Unity or
                                                Web"
                                            </HelperText>
                                        </FieldLabelWithHelper>
                                        <Select
                                            value={st.required_event}
                                            onChange={(e) => setStepField(i, "required_event", e.target.value)}
                                        >
                                            <option value="">Select required action...</option>
                                            {requiredEventOptions.map((ev) => (
                                                <option key={ev} value={ev}>
                                                    {ev.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                                                </option>
                                            ))}
                                        </Select>

                                        {st.required_event === "CUSTOM" && (
                                            <CustomEventInput>
                                                <Input
                                                    value={st.custom_required_event}
                                                    onChange={(e) => {
                                                        const formatted = formatCustomEvent(e.target.value);
                                                        setStepField(i, "custom_required_event", formatted);
                                                    }}
                                                    placeholder="CUSTOM_ACTION_NAME (uppercase, no spaces)"
                                                />
                                                <CustomEventHint>
                                                    Must be uppercase with no spaces (use underscores)
                                                </CustomEventHint>
                                            </CustomEventInput>
                                        )}
                                    </FieldBlock>
                                )}

                                {/* Conditional fields */}
                                {effectiveEvent === "TELEPORT" && (
                                    <FieldBlock>
                                        <FieldLabel>Teleport Destination Room ID *</FieldLabel>
                                        <Input
                                            value={st.teleportDestinationRoomId}
                                            onChange={(e) =>
                                                setStepField(i, "teleportDestinationRoomId", e.target.value)
                                            }
                                            placeholder="Destination room UUID"
                                        />
                                    </FieldBlock>
                                )}

                                {effectiveEvent === "LINKOUT" && (
                                    <FieldBlock>
                                        <FieldLabel>URL (Linkout) *</FieldLabel>
                                        <Input
                                            value={st.url}
                                            onChange={(e) => setStepField(i, "url", e.target.value)}
                                            placeholder="https://www.example.com"
                                        />
                                        <HelperText style={{ marginTop: "6px" }}>
                                            This URL will be added to the metadata as "url"
                                        </HelperText>
                                    </FieldBlock>
                                )}

                                {effectiveEvent === "OPEN_SMALL_IFRAME" && (
                                    <FieldBlock>
                                        <FieldLabel>URL (Small Iframe) *</FieldLabel>
                                        <Input
                                            value={st.url}
                                            onChange={(e) => setStepField(i, "url", e.target.value)}
                                            placeholder="https://www.example.com"
                                        />
                                        <HelperText style={{ marginTop: "6px" }}>
                                            This URL will be added to the metadata as "url"
                                        </HelperText>
                                    </FieldBlock>
                                )}

                                {effectiveEvent === "DYNAMIC_LINKOUT" && (
                                    <FieldBlock>
                                        <FieldLabel>DynamicLinkoutAction</FieldLabel>
                                        <Input
                                            value={st.dynamicLinkoutAction}
                                            onChange={(e) => setStepField(i, "dynamicLinkoutAction", e.target.value)}
                                            placeholder="Your dynamic linkout action key"
                                        />
                                    </FieldBlock>
                                )}

                                {/* Metadata with toggle switches - Grid Layout */}
                                <CollapsibleMetaBlock>
                                    <CollapsibleHeader
                                        onClick={() => {
                                            setSteps((prev) => {
                                                const copy = [...prev];
                                                copy[i] = {
                                                    ...copy[i],
                                                    _showModalSettings: !copy[i]._showModalSettings,
                                                };
                                                return copy;
                                            });
                                        }}
                                    >
                                        <MetaHeader>
                                            Quest Modal Settings{" "}
                                            <span
                                                style={{
                                                    fontSize: 12,
                                                    color: "white",
                                                    fontWeight: 400,
                                                    letterSpacing: "0.02em",
                                                    fontFamily: "Barlow, sans-serif",
                                                }}
                                            >
                                                (Expand for more options)
                                            </span>
                                        </MetaHeader>
                                        <CollapseToggle>{st._showModalSettings ? "▲" : "▼"}</CollapseToggle>
                                    </CollapsibleHeader>
                                    {st._showModalSettings && (
                                        <MetaGrid>
                                            {/* Celebrate Toggle */}
                                            <MetaGridItem active={!!st.metadata.celebrate}>
                                                <MetaCheckboxRow>
                                                    <MetaCheckbox
                                                        type="checkbox"
                                                        checked={!!st.metadata.celebrate}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setStepMetadata(i, "celebrate", true);
                                                            } else {
                                                                // Remove the key entirely when off
                                                                setSteps((prev) => {
                                                                    const copy = [...prev];
                                                                    const { celebrate, ...rest } = copy[i].metadata;
                                                                    copy[i] = { ...copy[i], metadata: rest };
                                                                    return copy;
                                                                });
                                                            }
                                                        }}
                                                    />
                                                    <MetaLabel>Celebrate 🎉</MetaLabel>
                                                </MetaCheckboxRow>
                                            </MetaGridItem>

                                            {/* GIF Toggle */}
                                            <MetaGridItem active={st.metadata.gif !== undefined}>
                                                <MetaCheckboxRow>
                                                    <MetaCheckbox
                                                        type="checkbox"
                                                        checked={st.metadata.gif !== undefined}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setStepMetadata(i, "gif", "");
                                                            } else {
                                                                // Remove the key entirely when off
                                                                setSteps((prev) => {
                                                                    const copy = [...prev];
                                                                    const { gif, ...rest } = copy[i].metadata;
                                                                    copy[i] = { ...copy[i], metadata: rest };
                                                                    return copy;
                                                                });
                                                            }
                                                        }}
                                                    />
                                                    <MetaLabel>GIF</MetaLabel>
                                                </MetaCheckboxRow>
                                                {st.metadata.gif !== undefined && (
                                                    <>
                                                        <div
                                                            style={{ display: "flex", flexDirection: "column", gap: 4 }}
                                                        >
                                                            <div
                                                                style={{
                                                                    fontSize: "12px",
                                                                    fontStyle: "italic",
                                                                    marginBottom: "4px",
                                                                    color: "#888",
                                                                }}
                                                            >
                                                                Files must be under 1MB
                                                            </div>
                                                            <FileInputStyled
                                                                type="file"
                                                                accept="image/gif"
                                                                style={{ width: "100%", maxWidth: "100%" }}
                                                                disabled={st.metadata._gifLoading}
                                                                onChange={async (e) => {
                                                                    if (e.target.files[0]) {
                                                                        setStepMetadata(i, "_gifLoading", true);
                                                                        setStepMetadata(i, "_gifStatus", "loading");
                                                                        // Timeout logic
                                                                        const gifTimeout = setTimeout(() => {
                                                                            setStepMetadata(i, "gif", "");
                                                                            setStepMetadata(i, "_gifLoading", false);
                                                                            setStepMetadata(i, "_gifStatus", "failed");
                                                                        }, 120000);
                                                                        const result = await handleMetaUpload(
                                                                            i,
                                                                            "gif",
                                                                            e.target.files[0]
                                                                        );
                                                                        clearTimeout(gifTimeout);
                                                                        // Only show success if presignedUrl is present and not failed
                                                                        if (
                                                                            result &&
                                                                            result.presignedUrl &&
                                                                            st.metadata.gif
                                                                        ) {
                                                                            setStepMetadata(i, "_gifLoading", false);
                                                                            setStepMetadata(i, "_gifStatus", "success");
                                                                            setTimeout(
                                                                                () =>
                                                                                    setStepMetadata(
                                                                                        i,
                                                                                        "_gifStatus",
                                                                                        null
                                                                                    ),
                                                                                2000
                                                                            );
                                                                        } else {
                                                                            setStepMetadata(i, "_gifLoading", false);
                                                                            setStepMetadata(i, "_gifStatus", "failed");
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                            {st.metadata._gifStatus === "loading" && (
                                                                <span
                                                                    style={{
                                                                        color: "#B9AFFF",
                                                                        fontWeight: 600,
                                                                        fontSize: 14,
                                                                    }}
                                                                >
                                                                    Uploading...
                                                                </span>
                                                            )}
                                                            {st.metadata._gifStatus === "success" && (
                                                                <span
                                                                    style={{
                                                                        color: "#4CAF50",
                                                                        fontWeight: 600,
                                                                        fontSize: 14,
                                                                    }}
                                                                >
                                                                    Upload successful!
                                                                </span>
                                                            )}
                                                            {metaUploadErrors[`${i}_gif`] && (
                                                                <ErrorMessage>
                                                                    {metaUploadErrors[`${i}_gif`]}
                                                                </ErrorMessage>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </MetaGridItem>

                                            {/* Video Toggle */}
                                            <MetaGridItem active={st.metadata.video !== undefined}>
                                                <MetaCheckboxRow>
                                                    <MetaCheckbox
                                                        type="checkbox"
                                                        checked={st.metadata.video !== undefined}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setStepMetadata(i, "video", "");
                                                            } else {
                                                                // Remove video and videoLarge keys when off
                                                                setSteps((prev) => {
                                                                    const copy = [...prev];
                                                                    const { video, videoLarge, ...rest } =
                                                                        copy[i].metadata;
                                                                    copy[i] = { ...copy[i], metadata: rest };
                                                                    return copy;
                                                                });
                                                            }
                                                        }}
                                                    />
                                                    <MetaLabel>Video</MetaLabel>
                                                </MetaCheckboxRow>
                                                {st.metadata.video !== undefined && (
                                                    <VideoInputRow>
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                gap: 4,
                                                                flex: 1,
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    fontSize: "12px",
                                                                    fontStyle: "italic",
                                                                    marginBottom: "4px",
                                                                    color: "#888",
                                                                }}
                                                            >
                                                                Files must be under 1MB
                                                            </div>
                                                            <FileInputStyled
                                                                type="file"
                                                                accept="video/*"
                                                                style={{ width: "100%", maxWidth: "100%" }}
                                                                disabled={st.metadata._videoLoading}
                                                                onChange={async (e) => {
                                                                    if (e.target.files[0]) {
                                                                        setStepMetadata(i, "_videoLoading", true);
                                                                        setStepMetadata(i, "_videoStatus", "loading");
                                                                        // Timeout logic
                                                                        const videoTimeout = setTimeout(() => {
                                                                            setStepMetadata(i, "video", "");
                                                                            setStepMetadata(i, "_videoLoading", false);
                                                                            setStepMetadata(
                                                                                i,
                                                                                "_videoStatus",
                                                                                "failed"
                                                                            );
                                                                        }, 120000);
                                                                        const result = await handleMetaUpload(
                                                                            i,
                                                                            "video",
                                                                            e.target.files[0]
                                                                        );
                                                                        clearTimeout(videoTimeout);
                                                                        // Only show success if presignedUrl is present and st.metadata.video is set
                                                                        if (
                                                                            result &&
                                                                            result.presignedUrl &&
                                                                            st.metadata.video
                                                                        ) {
                                                                            setStepMetadata(i, "_videoLoading", false);
                                                                            setStepMetadata(
                                                                                i,
                                                                                "_videoStatus",
                                                                                "success"
                                                                            );
                                                                            setTimeout(
                                                                                () =>
                                                                                    setStepMetadata(
                                                                                        i,
                                                                                        "_videoStatus",
                                                                                        null
                                                                                    ),
                                                                                2000
                                                                            );
                                                                        } else {
                                                                            setStepMetadata(i, "_videoLoading", false);
                                                                            setStepMetadata(
                                                                                i,
                                                                                "_videoStatus",
                                                                                "failed"
                                                                            );
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                            {st.metadata._videoStatus === "loading" && (
                                                                <span
                                                                    style={{
                                                                        color: "#B9AFFF",
                                                                        fontWeight: 600,
                                                                        fontSize: 14,
                                                                    }}
                                                                >
                                                                    Uploading...
                                                                </span>
                                                            )}
                                                            {st.metadata._videoStatus === "success" && (
                                                                <span
                                                                    style={{
                                                                        color: "#4CAF50",
                                                                        fontWeight: 600,
                                                                        fontSize: 14,
                                                                    }}
                                                                >
                                                                    Upload successful!
                                                                </span>
                                                            )}
                                                            {metaUploadErrors[`${i}_video`] && (
                                                                <ErrorMessage>
                                                                    {metaUploadErrors[`${i}_video`]}
                                                                </ErrorMessage>
                                                            )}
                                                        </div>
                                                        <VideoLargeCheckbox>
                                                            <input
                                                                type="checkbox"
                                                                checked={!!st.metadata.videoLarge}
                                                                onChange={(e) =>
                                                                    setStepMetadata(i, "videoLarge", e.target.checked)
                                                                }
                                                            />
                                                            <span>Show Large Video</span>
                                                        </VideoLargeCheckbox>
                                                    </VideoInputRow>
                                                )}
                                            </MetaGridItem>
                                            {/* Image Toggle */}
                                            <MetaGridItem active={st.metadata.image !== undefined}>
                                                <MetaCheckboxRow>
                                                    <MetaCheckbox
                                                        type="checkbox"
                                                        checked={st.metadata.image !== undefined}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setStepMetadata(i, "image", "");
                                                            } else {
                                                                setSteps((prev) => {
                                                                    const copy = [...prev];
                                                                    const {
                                                                        image,
                                                                        _imageLoading,
                                                                        _imageStatus,
                                                                        ...rest
                                                                    } = copy[i].metadata;
                                                                    copy[i] = { ...copy[i], metadata: rest };
                                                                    return copy;
                                                                });
                                                            }
                                                        }}
                                                    />
                                                    <MetaLabel>Image</MetaLabel>
                                                </MetaCheckboxRow>
                                                {st.metadata.image !== undefined && (
                                                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                                        <div
                                                            style={{
                                                                fontSize: "12px",
                                                                fontStyle: "italic",
                                                                marginBottom: "4px",
                                                                color: "#888",
                                                            }}
                                                        >
                                                            Files must be under 1MB
                                                        </div>
                                                        <FileInputStyled
                                                            type="file"
                                                            accept="image/*"
                                                            style={{ width: "100%", maxWidth: "100%" }}
                                                            disabled={st.metadata._imageLoading}
                                                            onChange={async (e) => {
                                                                if (e.target.files[0]) {
                                                                    setStepMetadata(i, "_imageLoading", true);
                                                                    setStepMetadata(i, "_imageStatus", "loading");
                                                                    // Timeout logic
                                                                    const imageTimeout = setTimeout(() => {
                                                                        setStepMetadata(i, "image", "");
                                                                        setStepMetadata(i, "_imageLoading", false);
                                                                        setStepMetadata(i, "_imageStatus", "failed");
                                                                    }, 120000);
                                                                    const result = await handleMetaUpload(
                                                                        i,
                                                                        "image",
                                                                        e.target.files[0]
                                                                    );
                                                                    clearTimeout(imageTimeout);
                                                                    // Only show success if presignedUrl is present and st.metadata.image is set
                                                                    if (
                                                                        result &&
                                                                        result.presignedUrl &&
                                                                        st.metadata.image
                                                                    ) {
                                                                        setStepMetadata(i, "_imageLoading", false);
                                                                        setStepMetadata(i, "_imageStatus", "success");
                                                                        setTimeout(
                                                                            () =>
                                                                                setStepMetadata(
                                                                                    i,
                                                                                    "_imageStatus",
                                                                                    null
                                                                                ),
                                                                            2000
                                                                        );
                                                                    } else {
                                                                        setStepMetadata(i, "_imageLoading", false);
                                                                        setStepMetadata(i, "_imageStatus", "failed");
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                        {st.metadata._imageStatus === "loading" && (
                                                            <span
                                                                style={{
                                                                    color: "#B9AFFF",
                                                                    fontWeight: 600,
                                                                    fontSize: 14,
                                                                }}
                                                            >
                                                                Uploading...
                                                            </span>
                                                        )}
                                                        {st.metadata._imageStatus === "success" && (
                                                            <span
                                                                style={{
                                                                    color: "#4CAF50",
                                                                    fontWeight: 600,
                                                                    fontSize: 14,
                                                                }}
                                                            >
                                                                Upload successful!
                                                            </span>
                                                        )}
                                                        {metaUploadErrors[`${i}_image`] && (
                                                            <ErrorMessage>
                                                                {metaUploadErrors[`${i}_image`]}
                                                            </ErrorMessage>
                                                        )}
                                                    </div>
                                                )}
                                            </MetaGridItem>

                                            {/* URL Toggle - Only for LINKOUT */}
                                            {effectiveEvent === "LINKOUT" && (
                                                <MetaGridItem fullWidth active={!!st.metadata.url}>
                                                    <MetaCheckboxRow>
                                                        <MetaCheckbox
                                                            type="checkbox"
                                                            checked={st.metadata.url !== undefined}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setStepMetadata(i, "url", "");
                                                                } else {
                                                                    // Remove the key entirely when off
                                                                    setSteps((prev) => {
                                                                        const copy = [...prev];
                                                                        const { url, ...rest } = copy[i].metadata;
                                                                        copy[i] = { ...copy[i], metadata: rest };
                                                                        return copy;
                                                                    });
                                                                }
                                                            }}
                                                        />
                                                        <MetaLabel>Linkout URL</MetaLabel>
                                                    </MetaCheckboxRow>
                                                    {st.metadata.url !== undefined && (
                                                        <Input
                                                            value={st.metadata.url || ""}
                                                            onChange={(e) => setStepMetadata(i, "url", e.target.value)}
                                                            placeholder="https://..."
                                                        />
                                                    )}
                                                </MetaGridItem>
                                            )}
                                        </MetaGrid>
                                    )}
                                    {/* Show what will be sent to API */}
                                    <MetaPreview>
                                        <MetaPreviewLabel>API Payload Preview:</MetaPreviewLabel>
                                        <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                            {JSON.stringify(buildMetadataPreview(st.metadata), null, 2) || "{}"}
                                        </pre>
                                    </MetaPreview>
                                </CollapsibleMetaBlock>
                            </StepBody>
                        </StepCard>
                    );
                })}
            </StepsContainer>

            {/* Add New Step and Save buttons */}
            <ButtonsContainer>
                <Button
                    margin={"0"}
                    buttonWidth={100}
                    variant="DefaultButton"
                    label="Add New Step"
                    onClick={addNewStep}
                />
                <Button
                    margin={"0"}
                    variant="PinkButton"
                    label={isSaving ? "Saving..." : "Save Quest + Steps"}
                    onClick={handleSaveAll}
                    disabled={isSaving || isLoading}
                />
            </ButtonsContainer>

            <Banner
                width={"100%"}
                message={bannerMessage}
                type={bannerType}
                openBanner={openBanner}
                setOpenBanner={setOpenBanner}
                topDisplay
                showCloseButton
                timer={8000}
            />

            {/* Room Conflict Modal */}
            {showConflictModal && roomConflict && (
                <ModalOverlay onClick={() => setShowConflictModal(false)}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <ModalTitle>Room already has an active quest</ModalTitle>
                        <ModalBody>
                            This room already has an active quest: <strong>{roomConflict.name}</strong>. Only one active
                            quest is allowed per room.
                        </ModalBody>
                        <ModalButtons>
                            <Button
                                margin={"0"}
                                buttonWidth={"100%"}
                                variant="OutlinedButton"
                                label={`Deactivate "${roomConflict.name}"`}
                                onClick={handleDeactivateConflict}
                            />
                            <Button
                                margin={"0"}
                                variant="PinkButton"
                                buttonWidth={"100%"}
                                label="Go to existing quest"
                                onClick={() => {
                                    setShowConflictModal(false);
                                    navigate(`/console/quest-builder/${roomConflict.questId}`);
                                }}
                            />
                            <Button
                                margin={"0"}
                                variant="DefaultButton"
                                buttonWidth={"100%"}
                                label="Keep this quest inactive"
                                onClick={() => setShowConflictModal(false)}
                            />
                        </ModalButtons>
                    </ModalContent>
                </ModalOverlay>
            )}

            {/* Delete Quest Confirmation Modal */}
            {showDeleteModal && (
                <ModalOverlay onClick={closeDeleteModal}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <ModalTitle>Are you sure you want to delete this quest?</ModalTitle>
                        <ModalBody>
                            <WarningText>
                                This action is <YellowText>permanent</YellowText> and{" "}
                                <YellowText>irreversible.</YellowText>
                            </WarningText>
                            <WarningText style={{ marginTop: "1rem" }}>
                                Please type <strong>DELETE</strong> below to proceed.
                            </WarningText>
                            {deleteError && <DeleteErrorText>{deleteError}</DeleteErrorText>}
                            <DeleteInput
                                type="text"
                                value={deleteText}
                                onChange={(e) => setDeleteText(e.target.value)}
                                placeholder="Type DELETE to confirm"
                            />
                        </ModalBody>
                        <ModalButtons>
                            <Button
                                margin={"0"}
                                variant="DefaultButton"
                                buttonWidth={"100%"}
                                label="GO BACK"
                                onClick={closeDeleteModal}
                            />
                            <Button
                                margin={"0"}
                                variant="PinkButton"
                                buttonWidth={"100%"}
                                disabled={deleteText.toLowerCase() !== "delete"}
                                label="PERMANENTLY DELETE"
                                onClick={confirmDeleteQuest}
                            />
                        </ModalButtons>
                    </ModalContent>
                </ModalOverlay>
            )}
        </Wrapper>
    );
};

// ---------------------------
// Styles (matching AddSurvey components)
// ---------------------------

const spin = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    font-family: "Barlow-Regular", Helvetica;
    padding-bottom: 80px; /* Space for sticky button */
    position: relative;

    @media (max-width: 768px) {
        padding: 0 10px 80px 10px;
    }
`;

const LoadingOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 9999;
`;

const Spinner = styled.div`
    width: 50px;
    height: 50px;
    border: 4px solid ${setColor.grey1};
    border-top: 4px solid ${setColor.iconColor3};
    border-radius: 50%;
    animation: ${spin} 1s linear infinite;
`;

const LoadingText = styled.p`
    margin-top: 16px;
    color: ${setColor.inputText};
    font-size: 18px;
    font-weight: 600;
`;

const BackButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    border: 1px solid ${setColor.grey1};
    color: ${setColor.inputText};
    padding: 10px 16px;
    border-radius: 8px;
    cursor: pointer;
    font-family: "Barlow-Regular", Helvetica;
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 20px;
    width: fit-content;
    transition: all 0.2s ease;

    &:hover {
        background: ${setColor.passwordValidBox};
        border-color: ${setColor.iconColor3};
    }

    @media (max-width: 768px) {
        width: 100%;
        justify-content: center;
    }
`;

const TopActions = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 15px;
    margin: 20px 0;
    flex-wrap: wrap;
    align-items: center;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

const ActionsRight = styled.div`
    display: flex;
    gap: 15px;
    align-items: center;

    @media (max-width: 768px) {
        justify-content: center;
    }
`;

const DeleteButton = styled.button`
    background: transparent;
    border: none;
    border-radius: 8px;
    padding: 8px 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease;

    &:hover {
        background: rgba(255, 72, 66, 0.15);
    }
`;

const StyledTrashIcon = styled(TrashIcon)`
    width: 20px;
    height: 20px;
    path {
        fill: #fff;
    }
`;

const ButtonsContainer = styled.div`
    display: flex;
    /* position: sticky;
    bottom: 0; */
    justify-content: center;
    /* background: rgba(23, 22, 34, 0.95); */
    /* border: 2px solid ${setColor.grey1}; */
    border-radius: 6px;
    gap: 16px;
    padding: 10px 20px;
    margin-top: 40px;
    margin-bottom: 0px;
    width: 100%;

    @media (max-width: 768px) {
        flex-direction: column;
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: rgba(23, 22, 34, 0.95);
        padding: 10px 20px;
        margin: 0;
        z-index: 100;
        box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.3);
    }
`;

const ErrorMessage = styled.p`
    color: ${setColor.warning};
    font-size: 16px;
    font-weight: 600;
    margin: 1rem 0 0 0;
`;

const QuestCard = styled.div`
    display: flex;
    flex-direction: column;
    padding: 1rem;
    background: rgb(23, 22, 34);
    border-radius: 5px;
    margin-bottom: 20px;

    @media (max-width: 768px) {
        padding: 12px;
    }
`;

const QuestIdDisplay = styled.div`
    font-size: 13px;
    color: ${setColor.greyIcon};
    margin-bottom: 12px;
    font-family: "Barlow-Regular", Helvetica;
    padding: 6px 10px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
    width: fit-content;
    user-select: all;
`;

const FieldRow = styled.div`
    display: flex;
    width: 100%;
    flex-direction: column;
    margin-bottom: 16px;

    @media (max-width: 768px) {
        margin-bottom: 12px;
    }
`;

const Label = styled.div`
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 10px;
    color: ${setColor.lightPurpleCheckbox};

    @media (max-width: 768px) {
        font-size: 14px;
    }
`;

const Input = styled.input`
    padding: 8px 10px;
    border: 1px solid ${setColor.grey1};
    border-radius: 5px;
    font-size: 14px;
    color: ${setColor.inputText};
    width: 100%;
    background-color: transparent;
    font-family: "Barlow-Regular", Helvetica;

    &:disabled {
        background-color: ${setColor.grey1};
    }

    &:focus {
        border: 1px solid ${setColor.accent4};
        outline: none;
    }

    @media (max-width: 768px) {
        padding: 8px;
        font-size: 14px;
    }
`;

const TextArea = styled.textarea`
    padding: 10px;
    border: 1px solid ${setColor.grey1};
    border-radius: 5px;
    color: ${setColor.inputText};
    width: 100%;
    min-height: 38px;
    height: 38px;
    background-color: transparent;
    resize: vertical;
    font-family: "Barlow-Regular", Helvetica;

    &:disabled {
        background-color: ${setColor.grey1};
    }

    &:focus {
        border: 1px solid ${setColor.accent4};
        outline: none;
    }

    @media (max-width: 768px) {
        min-height: 36px;
        height: 36px;
    }
`;

const StatusPill = styled.div`
    width: fit-content;
    display: inline-flex;
    align-items: center;
    padding: 6px 12px;
    border-radius: 5px;
    font-weight: 600;
    color: ${(p) => (p.active ? "#0b3" : setColor.warning)};
    background: ${setColor.passwordValidBox};
    border: 1px solid ${setColor.grey1};
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        opacity: 0.8;
        transform: scale(1.02);
        border-color: ${(p) => (p.active ? "#0b3" : setColor.warning)};
    }
`;

const StepsContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const StepCard = styled.div`
    display: flex;
    flex-direction: column;
    padding: 1rem;
    background: rgb(23, 22, 34);
    border-radius: 5px;
    font-family: "Barlow-Regular", Helvetica;
    opacity: ${(p) => (p.isDragging ? 0.7 : 1)};

    @media (max-width: 768px) {
        padding: 12px;
    }
`;

const StepTopRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    gap: 10px;
    flex-wrap: wrap;

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: flex-start;
    }
`;

const StepTitle = styled.p`
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: ${setFont.barlowCondensed};
    font-size: 26px;
    font-weight: 700;
    letter-spacing: 1.6px;
    text-transform: uppercase;
    background: linear-gradient(45deg, rgba(240, 21, 63, 1) 10%, rgba(221, 66, 206, 1) 40%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    margin: 0;
`;

const DragHandle = styled.span`
    cursor: grab;
    user-select: none;
    padding: 6px 10px;
    border-radius: 5px;
    border: 1px dashed ${setColor.grey1};
    color: ${setColor.inputText};
`;

const StepButtons = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;

    @media (max-width: 600px) {
        width: 100%;
        justify-content: flex-start;
    }
`;

const SmallBtn = styled.button`
    height: 34px;
    width: 38px;
    border-radius: 5px;
    border: 1px solid ${setColor.grey1};
    background: transparent;
    color: ${setColor.inputText};
    font-weight: 700;
    cursor: pointer;
    font-family: "Barlow-Regular", Helvetica;

    &:hover {
        border: 1px solid ${setColor.accent4};
        color: ${setColor.accent4};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const DangerBtn = styled.button`
    height: 34px;
    padding: 0 12px;
    border-radius: 5px;
    font-size: 12px;
    border: 1px solid ${setColor.redColor};
    background-color: transparent;
    color: ${setColor.redColor};
    font-weight: 500;
    cursor: pointer;
    font-family: "Barlow-Regular", Helvetica;

    &:hover {
        background-color: ${setColor.warning};
        color: ${setColor.hightlight};
    }

    @media (max-width: 600px) {
        width: 100%;
        margin-top: 8px;
    }
`;

const StepBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const FieldBlock = styled.div`
    display: flex;
    flex-direction: column;
    padding: 5px;
`;

const FieldLabel = styled.div`
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 10px;
    color: #aaaaaa;

    @media (max-width: 768px) {
        font-size: 14px;
    }
`;

const ButtonTextHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    flex-wrap: wrap;
    gap: 10px;
`;

const ButtonTextLabel = styled.div`
    font-family: ${setFont.barlowCondensed};
    font-size: 20px;
    font-weight: 500;
    letter-spacing: 1.3px;
    color: ${setColor.lightPurpleCheckbox};
    margin: 0 0 7px 0;

    @media (max-width: 768px) {
        font-size: 18px;
    }
`;

const ButtonPreviewWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border: 2px dashed ${setColor.grey1};
    border-radius: 10px;
`;

const PreviewLabel = styled.span`
    font-size: 12px;
    color: ${setColor.greyIcon};
    letter-spacing: 1px;
    font-family: "Barlow", sans-serif;
`;

const ButtonPreview = styled.div`
    background: linear-gradient(45deg, #f0153f 5.13%, #dd42ce 100.27%);
    color: white;
    padding: 8px 20px;
    border-radius: 8px;
    font-family: ${setFont.barlowCondensed};
    font-size: 14px;
    font-weight: 600;
    text-align: center;
    letter-spacing: 1.3px;
    min-width: 80px;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const FieldLabelWithHelper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 10px;

    ${FieldLabel} {
        margin-bottom: 0;
    }
`;

const HelperText = styled.span`
    font-size: 12px;
    color: ${setColor.greyIcon};
    font-style: italic;
`;

const Select = styled.select`
    padding: 10px;
    border: 1px solid ${setColor.grey1};
    border-radius: 5px;
    color: ${setColor.inputText};
    width: 100%;
    background-color: ${setColor.inputBgColor || "#1a1a2e"};
    font-family: "Barlow-Regular", Helvetica;

    option {
        background-color: ${setColor.inputBgColor || "#1a1a2e"};
        color: ${setColor.inputText};
    }

    &:disabled {
        background-color: ${setColor.grey1};
    }

    &:focus {
        border: 1px solid ${setColor.accent4};
        outline: none;
    }
`;

const RadioRow = styled.div`
    display: flex;
    gap: 18px;
    flex-wrap: wrap;
    margin-top: 8px;
`;

const RadioItem = styled.label`
    display: flex;
    gap: 8px;
    align-items: center;
    font-weight: 600;
    color: ${setColor.inputText};
    font-family: "Barlow-Regular", Helvetica;
    cursor: pointer;
    font-size: 16px;
`;

const CustomEventInput = styled.div`
    margin-top: 10px;
`;

const CustomEventHint = styled.p`
    font-size: 12px;
    color: ${setColor.greyIcon};
    margin-top: 6px;
`;
const MetaHeader = styled.p`
    font-family: ${setFont.barlowCondensed};
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 1.3px;
    color: ${setColor.lightPurpleCheckbox};
    margin: 0;
`;

const MetaGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin: 8px 12px;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const MetaGridItem = styled.div`
    display: flex;
    flex-direction: column;
    padding: 6px 8px;
    background: ${(props) => (props.active ? "rgba(108, 105, 105, 0.20)" : "transparent")};
    border-radius: 5px;
    transition:
        background 0.3s,
        border-color 0.3s;
    ${(props) => props.fullWidth && `grid-column: 1 / -1;`}
`;

const MetaCheckboxRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
`;

const MetaCheckbox = styled.input`
    width: 20px;
    height: 20px;
    cursor: pointer;
    accent-color: ${setColor.successColor};
    flex-shrink: 0;
`;

const VideoInputRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;

    input[type="text"] {
        flex: 1;
    }
`;

const VideoLargeCheckbox = styled.label`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: ${setColor.inputText};
    cursor: pointer;
    white-space: nowrap;
    padding: 6px 10px;
    background: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
    border: 1px solid ${setColor.grey1};

    input {
        cursor: pointer;
    }

    &:hover {
        background: rgba(0, 0, 0, 0.5);
    }
`;

const MetaLabel = styled.div`
    font-weight: 600;
    font-size: 13px;
    color: ${setColor.inputText};
`;

const MetaPreview = styled.div`
    margin-top: 6px;
    background: transparent;
    padding: 10px;

    pre {
        margin: 0;
        font-size: 12px;
        overflow: auto;
        max-height: 220px;
        color: ${setColor.inputText};
    }
`;

const MetaPreviewLabel = styled.p`
    font-size: 12px;
    color: ${setColor.greyIcon};
    margin-bottom: 8px;
`;

const FileInputStyled = styled.input`
    &::-webkit-file-upload-button {
        color: white;
        background: ${setColor.grey1};
        border-radius: 4px;
        font-size: 12px;
        padding: 5px 8px;
        margin: 0 10px 0 0;
        cursor: pointer;
    }
    color: ${setColor.inputText};
    font-size: 12px;
`;

const PinkRadioItem = styled(RadioItem)`
    border: 2px solid ${setColor.accent4};
    border-radius: 6px;
    padding: 6px 12px;
    background: rgba(221, 66, 206, 0.08);
`;

const OrText = styled.span`
    color: ${setColor.lightPurpleCheckbox};
    font-weight: 700;
    align-self: center;
    margin: 0 10px;
`;

const CollapsibleMetaBlock = styled.div`
    border-radius: 5px;
    border: 1px solid ${setColor.grey1};
    background: rgb(23, 22, 34, 0.7);
    margin-top: 10px;
    margin-bottom: 10px;
    overflow: hidden;
`;

const CollapsibleHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    padding: 10px 18px 10px 10px;
    background: rgba(255, 255, 255, 0.03);
    border-bottom: 1px solid ${setColor.grey1};
    user-select: none;
`;

const CollapseToggle = styled.span`
    font-size: 18px;
    margin-left: 10px;
    color: #aaa;
`;

// Row for Quest Name + Description
const Row2Col = styled.div`
    display: flex;
    gap: 18px;
    width: 100%;
    @media (max-width: 900px) {
        flex-direction: column;
        gap: 0;
    }
`;

// Room conflict status text styles
const RoomOkText = styled.div`
    font-size: 12px;
    color: ${setColor.successColor || "#4CAF50"};
    margin-top: 6px;
    font-weight: 500;
`;

const RoomConflictText = styled.div`
    font-size: 12px;
    color: ${setColor.warning};
    margin-top: 6px;
    font-weight: 500;

    a {
        color: ${setColor.warning};
        text-decoration: underline;
        cursor: pointer;

        &:hover {
            opacity: 0.8;
        }
    }
`;

// Room conflict modal styles
const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10000;
`;

const ModalContent = styled.div`
    background: rgb(23, 22, 34);
    border: 1px solid ${setColor.grey1};
    border-radius: 10px;
    padding: 24px;
    max-width: 500px;
    width: 90%;
    font-family: "Barlow-Regular", Helvetica;
`;

const ModalTitle = styled.h3`
    font-family: ${setFont.barlowCondensed};
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 1.3px;
    color: ${setColor.lightPink};
    margin: 0 0 16px 0;
`;

const ModalBody = styled.p`
    font-size: 14px;
    color: ${setColor.inputText};
    margin: 0 0 24px 0;
    line-height: 1.5;

    strong {
        color: ${setColor.lightPurpleCheckbox};
    }
`;

const ModalButtons = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    flex-wrap: wrap;

    @media (max-width: 500px) {
        flex-direction: column;
    }
`;

// Delete modal styles
const WarningText = styled.p`
    font-size: 14px;
    color: ${setColor.inputText};
    margin: 0;
`;

const YellowText = styled.span`
    color: ${setColor.yellowText || "#f5a623"};
    font-weight: 700;
`;

const DeleteInput = styled.input`
    padding: 10px;
    border: 1px solid ${setColor.grey1};
    border-radius: 5px;
    color: ${setColor.inputText};
    margin: 1.5rem 0 0 0;
    width: 100%;
    background-color: transparent;
    font-family: "Barlow-Regular", Helvetica;

    &:focus {
        border: 1px solid ${setColor.accent4};
        outline: none;
    }
`;

const DeleteErrorText = styled.div`
    font-size: 14px;
    color: ${setColor.errorColor};
    margin-top: 1rem;
`;

export default QuestBuilder;
